import React, { useState } from 'react';
import { calculateDosage, getReviewStatus, SLEEP_BASE_FORMULA } from '../utils/dosageCalculator';
import { calculateWeightDosage, getWeightReviewStatus, WEIGHT_BASE_FORMULA } from '../utils/weightDosageCalculator';
import { calculateDigestionDosage, getDigestionReviewStatus, DIGESTION_BASE_FORMULA } from '../utils/digestionDosageCalculator';
import { calculateImmunityDosage, getImmunityReviewStatus, IMMUNITY_BASE_FORMULA } from '../utils/immunityDosageCalculator';
import { calculateMaleDosage, getMaleReviewStatus, MALE_BASE_FORMULA } from '../utils/maleDosageCalculator';
import { calculateFemaleDosage, getFemaleReviewStatus, FEMALE_BASE_FORMULA } from '../utils/femaleDosageCalculator';
import { calculateHangoverDosage, getHangoverReviewStatus, HANGOVER_BASE_FORMULA } from '../utils/hangoverDosageCalculator';

function ResultPage({ symptom, answers, onStartOver }) {
  const [email, setEmail] = useState('');

  // 根据症状类型计算剂量和审核状态
  const getResultsForSymptom = () => {
    switch (symptom.id) {
      case 'sleep':
        return {
          dosage: calculateDosage(answers),
          reviewStatus: getReviewStatus(answers, calculateDosage(answers)),
          baseFormula: SLEEP_BASE_FORMULA,
          customFormula: [
            { name: 'Spine Date Seed', amount: `${calculateDosage(answers).amount}g` }
          ],
          title: 'Sleep Support Tea Blend',
          totalWeight: 12 + calculateDosage(answers).amount
        };
      case 'weight':
        const weightDosage = calculateWeightDosage(answers);
        return {
          dosage: weightDosage,
          reviewStatus: getWeightReviewStatus(answers, weightDosage),
          baseFormula: WEIGHT_BASE_FORMULA,
          customFormula: [
            { name: 'Coix Seed', amount: `${weightDosage.yiyiren}g` },
            { name: 'Hyacinth Bean Flower', amount: `${weightDosage.biandouhua}g` },
            { name: 'Lotus Leaf', amount: `${weightDosage.heye}g` }
          ],
          title: 'Weight Support Tea Formula',
          totalWeight: 9 + weightDosage.total
        };
      case 'digestion':
        const digestionDosage = calculateDigestionDosage(answers);
        return {
          dosage: digestionDosage,
          reviewStatus: getDigestionReviewStatus(answers, digestionDosage),
          baseFormula: DIGESTION_BASE_FORMULA,
          customFormula: [
            { name: 'Hawthorn Fruit', amount: `${digestionDosage.shanzha}g` },
            { name: 'Malted Barley', amount: `${digestionDosage.maiya}g` },
            { name: 'Aged Tangerine Peel', amount: `${digestionDosage.chenpi}g` }
          ],
          title: 'Digestive Health Support Tea Formula',
          totalWeight: 11.3 + digestionDosage.total
        };

      case 'immunity':
        const immunityDosage = calculateImmunityDosage(answers);
        return {
          dosage: immunityDosage,
          reviewStatus: getImmunityReviewStatus(answers, immunityDosage),
          baseFormula: IMMUNITY_BASE_FORMULA,
          customFormula: [
            { name: 'Astragalus Root', amount: `${immunityDosage.huangqi}g` },
            { name: 'American Ginseng', amount: `${immunityDosage.xiyangshen}g` },
            { name: 'Ophiopogon Root', amount: `${immunityDosage.maidong}g` }
          ],
          title: 'Immune Support Tea Formula',
          totalWeight: 5 + immunityDosage.total
        };

      case 'male':
        const maleDosage = calculateMaleDosage(answers);
        return {
          dosage: maleDosage,
          reviewStatus: getMaleReviewStatus(answers, maleDosage),
          baseFormula: MALE_BASE_FORMULA,
          customFormula: [
            { name: 'Epimedium', amount: `${maleDosage.yinyanghou}g` },
            { name: 'Eucommia Leaf', amount: `${maleDosage.duzhongye}g` },
            { name: 'Raspberry', amount: `${maleDosage.fupenzi}g` }
          ],
          title: 'Male Vitality Tea Formula',
          totalWeight: 13 + maleDosage.total
        };

      case 'female':
        const femaleDosage = calculateFemaleDosage(answers);
        return {
          dosage: femaleDosage,
          reviewStatus: getFemaleReviewStatus(answers, femaleDosage),
          baseFormula: FEMALE_BASE_FORMULA,
          customFormula: [
            { name: 'Motherwort Herb', amount: `${femaleDosage.yimucao}g` },
            { name: 'Cyperus Rhizome', amount: `${femaleDosage.xiangfu}g` },
            { name: 'Rose Flower', amount: `${femaleDosage.meiguihua}g` }
          ],
          title: 'Female Hormone Balance Tea Formula',
          totalWeight: 9 + femaleDosage.total
        };

      case 'hangover':  
        const hangoverDosage = calculateHangoverDosage(answers);
        return {
          dosage: hangoverDosage,
          reviewStatus: getHangoverReviewStatus(answers, hangoverDosage),
          baseFormula: HANGOVER_BASE_FORMULA,
          customFormula: [
            { name: 'Chrysanthemum Flower', amount: `${hangoverDosage.juhua}g` },
            { name: 'Imperata Rhizome', amount: `${hangoverDosage.baimaogen}g` },
            { name: 'Licorice Root', amount: `${hangoverDosage.gancao}g` }
          ],
          title: 'Liver Support Tea Formula',
          totalWeight: 12 + hangoverDosage.total
        };

      default:
        return null;
    }
  };

  const results = getResultsForSymptom();
  if (!results) return null;

  const { title, dosage } = results;
  
  const flavorMap = { light: 'light', medium: 'medium', heavy: 'strong' };
  const suggestedFlavor = dosage?.level ? (flavorMap[dosage.level] || 'medium') : 'medium';
  
  const redirectUrl = `https://www.tealybox.com/pages/quiz-result?name=${encodeURIComponent(sessionStorage.getItem('herbalzen_user_name') || '')}&email=${encodeURIComponent(email)}&survey_completed=true&symptom=${encodeURIComponent(symptom.id)}&flavor=${suggestedFlavor}`;

  return (
    <div className="result-container">
      <div className="result-icon">✨</div>
      <div className="result-title">Your perfectly matched Formula is Ready</div>
      <div className="result-subtitle">Survey complete! Based on your constitution and needs, we have perfectly matched a formula for you.</div>

      <div className="formula-box">
        <div className="formula-title">🍵 Your perfectly matched {title}</div>

        <div style={{ marginTop: '20px', marginBottom: '20px', textAlign: 'center' }}>
          <p style={{ marginBottom: '10px' }}>Please leave your email address to receive your matched formula details:</p>
          <input 
            type="email" 
            value={email} 
            onChange={(e) => setEmail(e.target.value)} 
            placeholder="Enter your email" 
            style={{ 
              padding: '12px', 
              width: '100%', 
              maxWidth: '300px', 
              borderRadius: '8px', 
              border: '1px solid #d2c5b3',
              backgroundColor: 'var(--input-bg)',
              color: 'var(--text-color)',
              fontSize: '16px',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          />
        </div>
      </div>

      <div className="button-group">
        <button className="btn btn-secondary" onClick={onStartOver}>
          Back to Home
        </button>
        <a 
          className="btn btn-primary"
          style={{ 
            textDecoration: 'none', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            boxSizing: 'border-box',
            opacity: email ? 1 : 0.5,
            cursor: email ? 'pointer' : 'not-allowed'
          }}
          href={email ? redirectUrl : "#"}
          onClick={(e) => {
            if (!email) {
              e.preventDefault();
              alert("Please enter your email address to proceed.");
            }
          }}
          target="_top"
        >
          Proceed
        </a>
      </div>

      <div style={{ marginTop: '24px', textAlign: 'center', fontSize: '0.8rem', color: '#888', padding: '0 20px', lineHeight: '1.4' }}>
        {/*
        <strong>Disclaimer:</strong> AI can make mistakes; please verify important information. This tool is for wellness purposes only and does not provide medical advice, diagnosis, or treatment. Always consult a healthcare professional for medical concerns.
        */}
      </div>
    </div>
  );
}

export default ResultPage;